import { getImage, getSharedImage } from '../../Common/Images/utilities/imageUtils.js';
import {
  cloneEditorValue,
  isTextNode,
  validateEditorDocument,
} from './htmlEditorUtils.js';

const BASE64_IMAGE_PATTERN = /^data:image\/(?:jpeg|jpg|png|gif|webp);base64,/i;

const visitElementNodes = (node, visitor) => {
  if (!node || isTextNode(node)) return;
  visitor(node);
  (node.children || []).forEach((child) => visitElementNodes(child, visitor));
};

export const isBase64ImageSource = (value) => (
  typeof value === 'string' && BASE64_IMAGE_PATTERN.test(value.trim())
);

export const collectEditorImageIds = (editorDocument) => {
  const document = validateEditorDocument(editorDocument).document;
  const imageIds = new Set();

  visitElementNodes(document.root, (node) => {
    const imageId = String(node.attributes?.['data-image-id'] || '').trim();
    if (imageId) imageIds.add(imageId);
  });

  return Array.from(imageIds);
};

export const prepareEditorDocumentForSave = (editorDocument) => {
  const document = cloneEditorValue(validateEditorDocument(editorDocument).document);
  const imageIds = new Set();
  const unresolvedNodeIds = [];
  let removedBase64Sources = 0;

  visitElementNodes(document.root, (node) => {
    const attributes = node.attributes || {};
    const imageId = String(attributes['data-image-id'] || '').trim();
    const hasBase64Source = isBase64ImageSource(attributes.src);

    if (imageId) imageIds.add(imageId);
    if (!hasBase64Source) return;

    if (!imageId) {
      unresolvedNodeIds.push(node.id);
      return;
    }

    delete attributes.src;
    node.attributes = attributes;
    removedBase64Sources += 1;
  });

  if (unresolvedNodeIds.length > 0) {
    const error = new Error(
      `${unresolvedNodeIds.length} inline image${unresolvedNodeIds.length === 1 ? '' : 's'} cannot be saved efficiently because no image ID is attached. Re-select the image from the image gallery.`
    );
    error.code = 'EDITOR/UNREFERENCED_BASE64_IMAGE';
    error.nodeIds = unresolvedNodeIds;
    throw error;
  }

  if (/data:image\/(?:jpeg|jpg|png|gif|webp);base64,/i.test(JSON.stringify(document))) {
    const error = new Error('Inline base64 image data remains outside an ID-backed image source. Re-select the image from the image gallery.');
    error.code = 'EDITOR/UNSUPPORTED_INLINE_BASE64_IMAGE';
    throw error;
  }

  return {
    document,
    imageIds: Array.from(imageIds),
    removedBase64Sources,
  };
};

export const compactPortfolioImagesForSave = (portfolioData) => {
  if (!portfolioData?.content?.root) return portfolioData;
  const prepared = prepareEditorDocumentForSave(portfolioData.content);
  return {
    ...portfolioData,
    content: prepared.document,
  };
};

export const hydrateEditorDocumentImages = async (editorDocument, userId, loaders = {}) => {
  const document = cloneEditorValue(validateEditorDocument(editorDocument).document);
  const pendingImageIds = new Set();
  visitElementNodes(document.root, (node) => {
    const imageId = String(node.attributes?.['data-image-id'] || '').trim();
    if (node.type === 'img' && imageId && !isBase64ImageSource(node.attributes?.src)) {
      pendingImageIds.add(imageId);
    }
  });
  const imageIds = Array.from(pendingImageIds);
  if (imageIds.length === 0) return document;

  const loadImage = loaders.loadImage || (async (imageId) => (
    userId ? getImage(userId, imageId) : getSharedImage(imageId)
  ));

  const imageEntries = await Promise.all(imageIds.map(async (imageId) => {
    try {
      return [imageId, await loadImage(imageId)];
    } catch (_) {
      return [imageId, null];
    }
  }));
  const imagesById = new Map(imageEntries);

  visitElementNodes(document.root, (node) => {
    const imageId = String(node.attributes?.['data-image-id'] || '').trim();
    const imageData = imagesById.get(imageId);
    if (node.type !== 'img' || !imageId || !imageData?.base64) return;

    node.attributes = {
      ...(node.attributes || {}),
      src: imageData.base64,
    };
  });

  return validateEditorDocument(document).document;
};

export const getSerializedByteSize = (value) => {
  const serialized = JSON.stringify(value);
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(serialized).length;
  }
  return serialized.length * 2;
};
